import type { ReactNode } from 'react';
import { AccountStatus, AccountType } from '../account/account';
import { CountryCodeType } from '../common/countries';
import { IdIdentifier, TimeIdentifiers } from '../common/identifiers';
import {
  ContestAccountRanking,
  ContestAccountRankingMetadata,
  ContestAccountType,
} from '../contest-account';
import { PrizeMetadata, PrizeTypes } from '../prize/prize';
import { FolderImageType } from '../storage/storage';
import { TradingSystem } from '../trading-system';
import { I18nEntry, I18nEntryArray } from '../translation/translation';
import { UserInformation } from '../user/user';
import {
  BadgeTitleEnum,
  ContestChallengeType,
  ContestCommonStatus,
  ContestEmailTemplatesType,
  ContestParticipantType,
  ContestRegistrationStatus,
  ContestStage,
  ContestStatisticTitle,
  ContestStatus,
} from './contest.enum';
import { UserProgressStatus } from './leaderboard.types';

export type ContestCenterButtonType =
  | 'top-performers'
  | 'invite-friends'
  | 'your-rank'
  | 'next-contest'
  | 'running-now';

export interface ContestSettings {
  startDate: Date;
  endDate: Date;
  description: string;
  registrationStartDate: Date;
  registrationEndDate: Date;
  participantsNumber: number;
  participantsType: ContestParticipantType;
  participantsTypeMetadata?: ContestParticipantTypeMetadata;
  restrictions?: ContestRestrictions;
  influencerCode?: string;
}

export interface ContestTradingSpecifications {
  accountType: AccountType;
  accountStatus: AccountStatus;
  challengeBy?: ContestChallengeType;
  systemGroup: string;
  terminationGroup: string;
  contestInitials: string;
  tradingSystem: TradingSystem;
  buyingPower: number;
  leverage: number;
  maxLoss?: number;
  dailyPause?: number;
  dailyPausePercentage?: number;
  dailyLoss?: number;
  dailyLossPercentage?: number;
  accountPreset?: number;
}

export interface ContestPrizes {
  fromRank: number;
  toRank: number;
  prizes: ContestPrize[];
  isSpecialAward?: boolean;
  iconName?: string;
}

/**
 * Display-safe prize tier: prize descriptions only, no internal prizeIds.
 * Derived from ContestPrizes by stripPrizeConfigurationForFrontend before the
 * raw config is removed from the API response.
 */
export interface ContestPrizesDisplay {
  fromRank: number;
  toRank: number;
  prizeDescriptions: string[];
  isSpecialAward?: boolean;
  iconName?: string;
  /** Lokalise i18n entries for translating each prizeDescription via getDynamicTranslation */
  i18n?: I18nEntry[];
}

export interface ContestPrize {
  prizeId: string;
  prizeType: PrizeTypes;
  metadata: Partial<PrizeMetadata>;
  expiryDate: Date;
}

export type ContestCreateDto = Omit<
  ContestEntity,
  | 'stage'
  | 'status'
  | 'contestId'
  | 'registrationStatus'
  | 'results'
  | 'leaderboardStatus'
  | 'archiveStatus'
  | 'terminationStatus'
  | 'prizeDistributionStatus'
  | 'prizes'
>;

export interface ContestImages {
  card: FolderImageType;
  banner: FolderImageType;
}

export interface ContestResults {
  participants: number;
  uniqueCountries?: number;
}

export interface NovaRule {
  id: string;
  rule: string;
}

export interface ContestPrizesOld {
  main: {
    winners: number;
    prizes: string[];
  };
  secondary: {
    amount: number;
    prize: string;
  };
}

export interface PrizePlacement {
  title: string;
  description: string;
  iconName?: string;
}

export interface ContestEntity {
  contestId: string;
  name: string;
  settings: ContestSettings;
  tradingSpecifications: ContestTradingSpecifications;
  rules: string[];
  termsAndConditions: string;
  novaRules?: NovaRule[];
  archiveStatus: ContestCommonStatus;
  terminationStatus: ContestCommonStatus;
  leaderboardStatus: ContestCommonStatus;
  prizesConfig: ContestPrizes[];
  prizes: ContestPrizesOld;
  prizesSubtitle?: string;
  specialAwardsConfig?: ContestPrizes[];
  /** Display-safe prize tiers derived from prizesConfig; populated by the API, no prizeIds exposed. */
  prizesConfigDisplay?: ContestPrizesDisplay[];
  /** Display-safe special award tiers derived from specialAwardsConfig; populated by the API, no prizeIds exposed. */
  specialAwardsConfigDisplay?: ContestPrizesDisplay[];
  prizeDistributionStatus: ContestCommonStatus;
  status: ContestStatus;
  images: ContestImages;
  stage: ContestStage;
  results: ContestResults;
  registrationStatus: ContestRegistrationStatus;
  notifications?: ContestEmailTemplatesType;
  i18n?: I18nEntry[];
  i18nArrays?: I18nEntryArray;
}

export interface ContestTimeInfo {
  label: string;
  startDate: Date;
  endDate: Date;
  isDisabled: boolean;
  showSeconds: boolean;
  icon: boolean;
  minutesText: 'short' | 'long';
}

export interface ContestCtaButton {
  text: string;
  variant: 'primary' | 'outlined' | 'naked' | 'link' | 'accent' | 'action';
  color?: string;
  disabled?: boolean;
  badge: {
    label: string;
    color: string;
    backgroundColor: string;
  } | null;
  icon?: {
    iconName: string; // SystemIconNames
    color?: string;
    position: 'before' | 'after';
  } | null;
  onClick?: () => void;
}

export interface ContestCenterButton {
  type: 'clickable' | 'non-clickable';
  content?: ReactNode;
  buttonType: ContestCenterButtonType;
  text?: string;
  userRank?: number;
  topPerformers?: TopPerformers[];
  onClick?: () => void;
  sx?: object;
}
/**
 * Registration state for contests where the user has not yet joined.
 *
 * - `upcoming` - Registration hasn't started yet
 * - `open_available` - Registration open and contest has spots available
 * - `open_full` - Registration open but contest is full
 * - `closed_not_started` - Registration closed and contest hasn't started yet
 * - `closed_started` - Registration closed and contest has started
 * - `fallback` - Fallback state when no other state matches
 */
export type RegistrationState =
  | 'upcoming'
  | 'open_available'
  | 'open_full'
  | 'closed_not_started'
  | 'closed_started'
  | 'fallback';

/**
 * Participant state for contests where the user has joined.
 *
 * - `open` - Contest is open for others to join
 * - `waiting` - Waiting for contest to start (registration closed or full)
 * - `started` - Contest has started
 */
export type ParticipantState = 'open' | 'waiting' | 'started';

export type AccountsForRunningContestsResponse = {
  accounts: AccountsForRunningContests[];
  runningContests: number;
};

export type AccountsForRunningContests = {
  publicId: string;
  firstName: string;
  lastName: string;
  image: string | null;
  country: string;
  badge: BadgeTitleEnum | null;
  contest: string;
  /** Lokalise keys for `contest` name; same shape as ContestEntity.i18n */
  contestI18n?: I18nEntry[];
  sloganTitle?: ContestStatisticTitle;
  sloganValue?: string;
  sloganValueParams?: Record<string, number | string | undefined>;
};

export type ContestParticipant = {
  publicId: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  profitAndLoss: number;
  badge: BadgeTitleEnum | null;
  position: number;
  avatar: string | null;
  /** Exact prize display name from distributed Prize document (closed contests) */
  prizeDisplayName?: string;
};

export interface ContestRankPrize {
  fromRank: number;
  toRank: number;
  displayName: string;
  prizeId?: string;
  isSpecialAward?: boolean;
  iconName?: string;
}

export type ClosedContestWithWinners = {
  contestId: string;
  contestName: string;
  name?: string; // Original name field for translation compatibility
  i18n?: I18nEntry[]; // i18n entries for dynamic translation
  i18nArrays?: I18nEntryArray; // e.g. prizesConfig for prize displayName (getDynamicTranslationForArray)
  contestStartDate: string;
  participantsMax: number;
  participantsJoined: number;
  nextContestId: string | null;
  prizes: ContestPrizesOld;
  rankPrizes?: ContestRankPrize[];
  topWinRate: number;
  bestRoi: number;
  totalTrades: number;
  mostTradedAsset: string;
  participants: ContestParticipant[];
};

export type UserContestRanking = {
  contestId: string;
  ranking: ContestAccountRanking;
};

export type TopPerformers = {
  contestId: string;
  publicId: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  badge: BadgeTitleEnum | null;
  profileUrl: string | null;
  position: number;
};

export type ContestInformation = {
  userContests: ContestWithUserAccount[];
  all: Record<ContestStage, ContestWithUserAccount[]>;
  nextContestId: string | null;
  topPerformers: TopPerformers[];
};

export interface EnhancedPrizeData {
  main: {
    position: string;
    prize: string;
    icon: string;
    color: string;
  }[];
  special: {
    name: string;
    description: string;
    icon: string;
    color: string;
  }[];
}

export interface Contest {
  contestId: string;
  name: string;
  stage: ContestStage;
  settings: {
    startDate: Date;
    endDate: Date;
    registrationStartDate: Date;
    registrationEndDate: Date;
    participantsNumber: number;
  };
  results: {
    participants: number;
  };
  registrationStatus: 'open' | 'closed' | 'upcoming';
  prizes?: {
    main?: {
      winners?: number;
    };
    secondary?: {
      amount?: number;
      prize?: string;
    };
  };
  participationEligibility?: {
    canParticipate: boolean;
  };
  userRank?: number | null;
}

export type PopulatedUserForLive = {
  publicId: string;
  firstName?: string;
  lastName?: string;
  country?: string;
  profileUrl?: string;
};

// Use this when a populated user (from Mongo populate) must expose publicId
export type UserInformationWithPublicId = UserInformation & {
  publicId: string;
};

export interface ContestParticipationMetadata {
  // For InsufficientPurchases
  requiredPurchases?: number;
  userPurchases?: number;

  // For UserStageNotAllowed
  requiredBadges?: UserProgressStatus[];

  // For ProgramGroupNotAllowed
  requiredProgramGroups?: string[];

  // For CountryNotAllowed
  allowedCountries?: string[]; // Country names, not codes
}

export type ContestParticipationEligibility = {
  canParticipate: boolean;
  reasonsForParticipateDenial?: ContestParticipationDenialReason[];
  sourceId?: string;
  metadata?: ContestParticipationMetadata;
};

export type ContestType = ContestEntity &
  TimeIdentifiers &
  IdIdentifier & {
    participationEligibility?: ContestParticipationEligibility;
  };

export type ContestToRegister = {
  contest?: ContestType;
};

export type ContestWithUserAccount = ContestType & {
  account?: ContestAccountType;
};

export type ContestAccountCredentials = {
  login: string;
  password: string;
  externalId: string;
  tradingSystem: TradingSystem;
};

export type ContestSpotlight = {
  userContests: ContestWithUserAccount[];
  all: Record<ContestStage, ContestWithUserAccount[]>;
};

export type ContestLeaderboardTsType = ContestAccountRankingMetadata[];

export type ContestLeaderBoardTsResponse = {
  contestId: string;
  leaderboard: ContestLeaderboardTsType;
  disqualified: number;
};

export type ContestLeaderboard = {
  contestId: string;
  leaderboard: Array<{
    userName?: string;
    firstName: string;
    lastName: string;
    country?: string;
    ranking: ContestAccountRanking;
    currentUser?: boolean;
    publicId: string;
  }>;
  disqualified: number;
};

export type ContestParticipantTypeMetadata = {
  usersPurchaseStartDate: Date;
  usersPurchaseEndDate: Date;
};

export enum ContestParticipationDenialReason {
  InBlackList = 'in_black_list',
  InsufficientPurchases = 'insufficient_purchases',
  UserStageNotAllowed = 'user_stage_not_allowed',
  PurchaseDateNotInRange = 'purchase_date_not_in_range',
  AlreadySignedUp = 'already_signed_up',
  CountryNotAllowed = 'country_not_allowed',
  ValidationFailed = 'validation_failed',
  ProgramGroupRequired = 'program_group_required',
  InfluencerCodeRequired = 'influencer_code_required',
  InvalidInfluencerCode = 'invalid_influencer_code',
}

export interface ContestRestrictions {
  minPurchases?: number;
  userProgressLevel?: UserProgressStatus[];
  countries?: CountryCodeType[];
  programGroups?: string[];
}
